import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { faqData } from '../../mocks/donateData';
import { fetchPublicPackages, submitDonation, type DonateSubmitResult } from '../../api/dripPlan';

type TierItem = {
  id: number;
  amount: number;
  label: string;
  bottles?: number;
  description: string;
  tag?: string;
  imageUrl?: string;
};

const DonatePage = () => {
  const navigate = useNavigate();
  const [packageTiers, setPackageTiers] = useState<TierItem[]>([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [packagesLoadError, setPackagesLoadError] = useState<string | null>(null);
  const [selectedTier, setSelectedTier] = useState<number>(0);
  const [customAmount, setCustomAmount] = useState('');
  const [showFAQ, setShowFAQ] = useState(false);
  const [showCertificate, setShowCertificate] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showCopyLinkModal, setShowCopyLinkModal] = useState(false);
  const [copyLinkText, setCopyLinkText] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    name: '',
    phone: '',
    email: '',
    type: 'personal'
  });
  const [invoiceSubmitted, setInvoiceSubmitted] = useState(false);
  const [donating, setDonating] = useState(false);
  const [donorName, setDonorName] = useState('');
  const [donorPhone, setDonorPhone] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [certificateInfo, setCertificateInfo] = useState<DonateSubmitResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchPublicPackages()
      .then((list) => {
        if (cancelled) return;
        const items: TierItem[] = list.map((p) => ({
          id: p.id,
          amount: Number(p.price) || 0,
          label: p.name,
          description: p.description || '',
          tag: p.tag?.trim() || undefined,
          imageUrl: p.imageUrl,
        }));
        setPackageTiers(items);
        if (!cancelled) setPackagesLoadError(null);
        if (items.length > 0 && selectedTier === 0) setSelectedTier(items[0].id);
      })
      .catch((err) => {
        if (!cancelled) {
          setPackageTiers([]);
          setPackagesLoadError(err?.message || '套餐加载失败');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingPackages(false);
      });
    return () => { cancelled = true; };
  }, []);

  // 爱心档位仅使用接口动态数据，不使用静态 mock
  const tiers: TierItem[] = packageTiers;

  useEffect(() => {
    if (!loadingPackages && packageTiers.length > 0 && selectedTier === 0) {
      setSelectedTier(packageTiers[0].id);
    }
  }, [loadingPackages, packageTiers.length, selectedTier]);

  const handleDonate = async (method: 'wechat' | 'alipay') => {
    const custom = customAmount ? parseFloat(customAmount) : 0;
    const tier = tiers.find((t) => t.id === selectedTier);
    const amount = custom > 0 ? custom : tier?.amount ?? 0;
    if (!(amount > 0)) {
      alert('请选择套餐或输入有效金额');
      return;
    }
    setDonating(true);
    try {
      const result = await submitDonation({
        donorName: donorName.trim() || '爱心人士',
        phone: donorPhone.trim() || undefined,
        packageId: custom > 0 ? undefined : selectedTier || undefined,
        packageName: custom > 0 ? '自定义捐赠' : tier?.label,
        customAmount: custom > 0 ? custom : undefined,
        payMethod: method,
        isAnonymous,
      });
      setCertificateInfo(result);
      setShowCertificate(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      alert((err as Error)?.message || '捐赠提交失败，请稍后重试');
    } finally {
      setDonating(false);
    }
  };

  const selectedAmount = certificateInfo?.amount ?? (customAmount
    ? parseFloat(customAmount)
    : tiers.find((t) => t.id === selectedTier)?.amount ?? 0);

  const handleShare = (platform: string) => {
    const shareText = `我刚刚通过"点滴公益"捐赠了¥${selectedAmount.toFixed(2)}，为城市守护者送去清凉关怀！快来一起参与吧！`;
    const shareUrl = window.location.origin;
    
    switch(platform) {
      case 'wechat':
        // 跳转到微信朋友圈分享
        const wechatShareUrl = `weixin://dl/moments`;
        window.location.href = wechatShareUrl;
        // 如果无法跳转，显示提示
        setTimeout(() => {
          alert('请打开微信，点击"发现"-"朋友圈"分享您的爱心证书');
        }, 500);
        break;
      case 'copy':
        const fullShareText = `${shareText} ${shareUrl}`;
        setCopyLinkText(fullShareText);
        setLinkCopied(false);
        setShowCopyLinkModal(true);
        break;
    }
    setShowShareModal(false);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(copyLinkText);
    setLinkCopied(true);
  };

  const handleInvoiceSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // 模拟提交票据申请
    setInvoiceSubmitted(true);
    setTimeout(() => {
      setShowInvoiceModal(false);
      setInvoiceSubmitted(false);
      setInvoiceForm({ name: '', phone: '', email: '', type: 'personal' });
    }, 2000);
  };

  if (showCertificate) {
    return (
      <div className="min-h-screen bg-[#F9F9F9]">
        <Navbar />
        <div className="mt-20 py-12 md:py-20">
          <div className="max-w-[800px] mx-auto px-6">
            <div className="bg-white rounded-2xl md:rounded-3xl shadow-2xl p-6 md:p-12 text-center">
              <div className="w-16 h-16 md:w-24 md:h-24 bg-gradient-to-br from-[#2A7A62] to-[#3A9A82] rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                <i className="ri-check-line text-3xl md:text-5xl text-white"></i>
              </div>
              <h1 className="text-2xl md:text-4xl font-bold text-[#2A7A62] mb-3 md:mb-4">感谢您的爱心捐赠!</h1>
              <p className="text-base md:text-xl text-[#6C6C6C] mb-6 md:mb-8">您的善举将温暖城市守护者的心</p>

              <div className="bg-gradient-to-br from-[#E8F4F0] to-[#F9F9F9] rounded-xl md:rounded-2xl p-4 md:p-8 mb-6 md:mb-8">
                <div className="grid grid-cols-2 gap-4 md:gap-6 text-left">
                  <div>
                    <p className="text-xs md:text-sm text-[#6C6C6C] mb-1">捐赠人</p>
                    <p className="text-sm md:text-lg font-bold text-[#1E1E1E]">{certificateInfo?.donorName || donorName || '爱心人士'}</p>
                  </div>
                  <div>
                    <p className="text-xs md:text-sm text-[#6C6C6C] mb-1">捐赠金额</p>
                    <p className="text-sm md:text-lg font-bold text-[#2A7A62]">¥{selectedAmount.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs md:text-sm text-[#6C6C6C] mb-1">对应瓶数</p>
                    <p className="text-sm md:text-lg font-bold text-[#1E1E1E]">
                      {Math.floor((selectedAmount / 19.9) * 12)}瓶
                    </p>
                  </div>
                  <div>
                    <p className="text-xs md:text-sm text-[#6C6C6C] mb-1">捐赠日期</p>
                    <p className="text-sm md:text-lg font-bold text-[#1E1E1E]">
                      {new Date().toLocaleDateString('zh-CN')}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs md:text-sm text-[#6C6C6C] mb-1">捐赠编号</p>
                    <p className="text-sm md:text-lg font-bold text-[#1E1E1E] font-mono">
                      {certificateInfo?.donationNo || `DD${Date.now().toString().slice(-10)}`}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-3 md:gap-4 justify-center">
                <button 
                  onClick={() => setShowShareModal(true)}
                  className="px-6 py-3 md:px-8 md:py-3 bg-[#2A7A62] text-white rounded-lg hover:bg-[#236854] transition-all cursor-pointer whitespace-nowrap text-sm md:text-base"
                >
                  <i className="ri-share-line mr-2"></i>
                  分享证书
                </button>
                <button 
                  onClick={() => setShowInvoiceModal(true)}
                  className="px-6 py-3 md:px-8 md:py-3 bg-white text-[#2A7A62] border-2 border-[#2A7A62] rounded-lg hover:bg-[#2A7A62]/10 transition-all cursor-pointer whitespace-nowrap text-sm md:text-base"
                >
                  <i className="ri-file-text-line mr-2"></i>
                  申请票据
                </button>
                <button
                  onClick={() => navigate(-1)}
                  className="px-6 py-3 md:px-8 md:py-3 bg-[#F9F9F9] text-[#1E1E1E] rounded-lg hover:bg-[#E0E0E0] transition-all cursor-pointer whitespace-nowrap text-sm md:text-base"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  返回上一步
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 分享弹窗 */}
        {showShareModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowShareModal(false)}>
            <div className="bg-white rounded-2xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-[#1E1E1E]">分享证书</h3>
                <button onClick={() => setShowShareModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F9F9F9] cursor-pointer">
                  <i className="ri-close-line text-xl text-[#6C6C6C]"></i>
                </button>
              </div>
              <div className="space-y-3">
                <button onClick={() => handleShare('wechat')} className="w-full flex items-center gap-4 p-4 bg-[#F9F9F9] rounded-xl hover:bg-[#E8F4F0] transition-colors cursor-pointer">
                  <div className="w-12 h-12 bg-[#07C160] rounded-full flex items-center justify-center">
                    <i className="ri-wechat-fill text-2xl text-white"></i>
                  </div>
                  <div className="text-left">
                    <span className="text-lg font-medium text-[#1E1E1E] block">分享到微信朋友圈</span>
                    <span className="text-xs text-[#6C6C6C]">分享您的爱心证书给好友</span>
                  </div>
                </button>
                <button onClick={() => handleShare('copy')} className="w-full flex items-center gap-4 p-4 bg-[#F9F9F9] rounded-xl hover:bg-[#E8F4F0] transition-colors cursor-pointer">
                  <div className="w-12 h-12 bg-[#6C6C6C] rounded-full flex items-center justify-center">
                    <i className="ri-link text-2xl text-white"></i>
                  </div>
                  <span className="text-lg font-medium text-[#1E1E1E]">复制链接</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 复制链接弹窗 */}
        {showCopyLinkModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowCopyLinkModal(false)}>
            <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-[#1E1E1E]">复制分享链接</h3>
                <button onClick={() => setShowCopyLinkModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F9F9F9] cursor-pointer">
                  <i className="ri-close-line text-xl text-[#6C6C6C]"></i>
                </button>
              </div>
              
              <div className="bg-[#F9F9F9] rounded-xl p-4 mb-4">
                <p className="text-sm text-[#1E1E1E] leading-relaxed break-all">{copyLinkText}</p>
              </div>
              
              {linkCopied ? (
                <div className="flex items-center justify-center gap-2 py-3 bg-[#2A7A62]/10 rounded-lg text-[#2A7A62]">
                  <i className="ri-check-line text-xl"></i>
                  <span className="font-medium">链接已复制到剪贴板</span>
                </div>
              ) : (
                <button
                  onClick={handleCopyLink}
                  className="w-full py-3 bg-[#2A7A62] text-white rounded-lg font-medium hover:bg-[#236854] transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-file-copy-line mr-2"></i>
                  复制链接
                </button>
              )}
            </div>
          </div>
        )}

        {/* 申请票据弹窗 */}
        {showInvoiceModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowInvoiceModal(false)}>
            <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-[#1E1E1E]">申请捐赠票据</h3>
                <button onClick={() => setShowInvoiceModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F9F9F9] cursor-pointer">
                  <i className="ri-close-line text-xl text-[#6C6C6C]"></i>
                </button>
              </div>
              
              {invoiceSubmitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-[#2A7A62] rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-3xl text-white"></i>
                  </div>
                  <h4 className="text-xl font-bold text-[#1E1E1E] mb-2">申请已提交</h4>
                  <p className="text-[#6C6C6C]">我们将在5个工作日内为您开具票据</p>
                </div>
              ) : (
                <form onSubmit={handleInvoiceSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1E1E1E] mb-2">票据类型</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input 
                          type="radio" 
                          name="type" 
                          value="personal" 
                          checked={invoiceForm.type === 'personal'}
                          onChange={(e) => setInvoiceForm({...invoiceForm, type: e.target.value})}
                          className="w-4 h-4 text-[#2A7A62]"
                        />
                        <span>个人</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input 
                          type="radio" 
                          name="type" 
                          value="company" 
                          checked={invoiceForm.type === 'company'}
                          onChange={(e) => setInvoiceForm({...invoiceForm, type: e.target.value})}
                          className="w-4 h-4 text-[#2A7A62]"
                        />
                        <span>企业</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1E1E1E] mb-2">
                      {invoiceForm.type === 'personal' ? '姓名' : '企业名称'} <span className="text-[#E06B3D]">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={invoiceForm.name}
                      onChange={(e) => setInvoiceForm({...invoiceForm, name: e.target.value})}
                      className="w-full px-4 py-3 border border-[#E0E0E0] rounded-lg focus:outline-none focus:border-[#2A7A62]"
                      placeholder={invoiceForm.type === 'personal' ? '请输入您的姓名' : '请输入企业名称'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1E1E1E] mb-2">联系电话 <span className="text-[#E06B3D]">*</span></label>
                    <input
                      type="tel"
                      required
                      value={invoiceForm.phone}
                      onChange={(e) => setInvoiceForm({...invoiceForm, phone: e.target.value})}
                      className="w-full px-4 py-3 border border-[#E0E0E0] rounded-lg focus:outline-none focus:border-[#2A7A62]"
                      placeholder="请输入联系电话"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1E1E1E] mb-2">电子邮箱 <span className="text-[#E06B3D]">*</span></label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={invoiceForm.email}
                      onChange={(e) => setInvoiceForm({...invoiceForm, email: e.target.value})}
                      className="w-full px-4 py-3 border border-[#E0E0E0] rounded-lg focus:outline-none focus:border-[#2A7A62]"
                      placeholder="票据将发送至此邮箱"
                    />
                  </div>
                  <div className="bg-[#FFF9E6] rounded-lg p-4">
                    <p className="text-sm text-[#6C6C6C]">
                      <i className="ri-information-line text-[#F4B942] mr-2"></i>
                      票据将由重庆市南岸区明明德志愿服务中心开具，可用于个人所得税抵扣。
                    </p>
                  </div>
                  <button
                    type="submit"
                    className="w-full py-3 bg-[#2A7A62] text-white rounded-lg font-medium hover:bg-[#236854] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    提交申请
                  </button>
                </form>
              )}
            </div>
          </div>
        )}

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <Navbar />

      {/* Hero Section */}
      <section className="mt-20 bg-gradient-to-br from-[#2A7A62] to-[#1E5A4A] text-white py-12 md:py-20">
        <div className="max-w-[1400px] mx-auto px-6 text-center">
          <h1 className="text-3xl md:text-5xl font-bold mb-4 md:mb-6">我要认捐</h1>
          <p className="text-base md:text-2xl text-white/90 mb-2 md:mb-4">
            19.9元<br className="md:hidden" /> = 12瓶水<br className="md:hidden" /> = 1份清凉关怀
          </p>
          <p className="text-sm md:text-lg text-white/80">
            您的每一份爱心,<br className="md:hidden" />都将精准送达城市守护者手中
          </p>
        </div>
      </section>

      {/* Donation Tiers */}
      <section className="py-12 md:py-20">
        <div className="max-w-[1200px] mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-[#2A7A62] text-center mb-6 md:mb-8">
            选择您的爱心档位
          </h2>
          {loadingPackages && <p className="text-center text-sm text-[#6C6C6C] mb-6 md:mb-8">正在加载套餐…</p>}
          {!loadingPackages && tiers.length === 0 && (
            <p className="text-center text-sm text-[#6C6C6C] mb-6 md:mb-8">
              暂无可选套餐时，可直接使用下方自定义金额
            </p>
          )}

          <div className="w-full flex justify-center mb-6 md:mb-8">
            {loadingPackages ? (
              <div className="w-full text-center py-8 text-[#6C6C6C]">加载套餐中...</div>
            ) : tiers.length === 0 ? (
              <div className="w-full text-center py-6 text-[#6C6C6C] text-sm">
                暂无套餐，请使用下方自定义金额
              </div>
            ) : (
              <div className="inline-flex flex-wrap justify-center gap-4 md:gap-6">
              {tiers.slice(0, 5).map((tier) => (
                <div
                  key={tier.id}
                  onClick={() => {
                    setSelectedTier(tier.id);
                    setCustomAmount('');
                  }}
                  className={`relative w-[280px] md:w-[260px] flex-shrink-0 bg-white rounded-xl md:rounded-2xl p-4 md:p-6 cursor-pointer transition-all ${
                    selectedTier === tier.id
                      ? 'border-2 border-[#2A7A62] shadow-2xl scale-105'
                      : 'border border-[#E0E0E0] hover:border-[#2A7A62] hover:shadow-lg'
                  }`}
                >
                  {tier.tag && (
                    <div className="absolute -top-2 md:-top-3 left-1/2 -translate-x-1/2 px-3 py-1 md:px-4 md:py-1 bg-[#F4B942] text-white text-xs md:text-sm font-bold rounded-full">
                      {tier.tag}
                    </div>
                  )}
                  <div className="text-center">
                    <div className="w-full h-24 md:h-28 rounded-lg mb-3 mx-auto overflow-hidden bg-[#F0F0F0] flex items-center justify-center">
                      {tier.imageUrl ? (
                        <img src={tier.imageUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-xs text-[#999]">暂无图片</span>
                      )}
                    </div>
                    <p className="text-xs md:text-sm text-[#6C6C6C] mb-1 md:mb-2 min-h-[1.25em]">{tier.description || ''}</p>
                    <p className="text-2xl md:text-4xl font-bold text-[#2A7A62] mb-1 md:mb-2">¥{tier.amount}</p>
                    <p className="text-lg md:text-2xl font-bold text-[#1E1E1E] mb-2 md:mb-3">{tier.label}</p>
                  </div>
                  {selectedTier === tier.id && (
                    <div className="absolute top-3 right-3 md:top-4 md:right-4 w-6 h-6 md:w-8 md:h-8 bg-[#2A7A62] rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-white text-sm md:text-lg"></i>
                    </div>
                  )}
                </div>
              ))}
              </div>
            )}
          </div>

          {/* Donor info */}
          <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 border border-[#E0E0E0] mb-6 md:mb-8">
            <h2 className="text-base md:text-lg font-bold text-[#1E1E1E] mb-3 md:mb-4">捐赠人信息（选填）</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                value={donorName}
                onChange={(e) => setDonorName(e.target.value)}
                placeholder="姓名或单位，留空显示「爱心人士」"
                className="w-full h-11 px-4 border-2 border-[#E0E0E0] rounded-lg focus:border-[#2A7A62] focus:outline-none"
              />
              <input
                type="tel"
                value={donorPhone}
                onChange={(e) => setDonorPhone(e.target.value)}
                placeholder="手机号（选填，用于申请票据）"
                className="w-full h-11 px-4 border-2 border-[#E0E0E0] rounded-lg focus:border-[#2A7A62] focus:outline-none"
              />
            </div>
            <label className="flex items-center gap-2 mt-4 cursor-pointer text-sm text-[#6C6C6C]">
              <input type="checkbox" checked={isAnonymous} onChange={(e) => setIsAnonymous(e.target.checked)} className="w-4 h-4" />
              匿名捐赠（公示时不显示姓名）
            </label>
          </div>

          {/* Custom Amount */}
          <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 border border-[#E0E0E0]">
            <label className="block text-base md:text-lg font-bold text-[#1E1E1E] mb-3 md:mb-4">
              自定义金额 (其他金额)
            </label>
            <div className="relative">
              <span className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 text-lg md:text-2xl text-[#6C6C6C]">
                ¥
              </span>
              <input
                type="number"
                value={customAmount}
                onChange={(e) => {
                  setCustomAmount(e.target.value);
                  setSelectedTier(0);
                }}
                placeholder="请输入金额"
                className="w-full h-12 md:h-14 pl-10 md:pl-12 pr-4 text-base md:text-xl border-2 border-[#E0E0E0] rounded-lg focus:border-[#2A7A62] focus:outline-none transition-colors"
              />
            </div>
            {customAmount && (
              <p className="mt-2 md:mt-3 text-xs md:text-sm text-[#6C6C6C]">
                约等于 <span className="font-bold text-[#2A7A62]">
                  {Math.floor((parseFloat(customAmount) / 19.9) * 12)}
                </span> 瓶水
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Payment Methods */}
      <section className="py-8 md:py-12">
        <div className="max-w-[1200px] mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-[#2A7A62] text-center mb-6 md:mb-8">选择支付方式</h2>

          <div className="grid grid-cols-2 md:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
            <button
              onClick={() => handleDonate('wechat')}
              disabled={donating}
              className="h-12 md:h-16 bg-[#07C160] text-white text-base md:text-xl font-bold rounded-lg md:rounded-xl hover:bg-[#06AD56] transition-all shadow-lg hover:shadow-xl cursor-pointer flex items-center justify-center gap-2 md:gap-3 whitespace-nowrap disabled:opacity-60"
            >
              <i className="ri-wechat-pay-line text-2xl md:text-3xl"></i>
              {donating ? '提交中...' : '微信支付'}
            </button>
            <button
              onClick={() => handleDonate('alipay')}
              disabled={donating}
              className="h-12 md:h-16 bg-[#1677FF] text-white text-base md:text-xl font-bold rounded-lg md:rounded-xl hover:bg-[#1461D8] transition-all shadow-lg hover:shadow-xl cursor-pointer flex items-center justify-center gap-2 md:gap-3 whitespace-nowrap disabled:opacity-60"
            >
              <i className="ri-alipay-line text-2xl md:text-3xl"></i>
              {donating ? '提交中...' : '支付宝支付'}
            </button>
          </div>

          <div className="bg-[#FFF9E6] border border-[#F4B942] rounded-lg md:rounded-xl p-4 md:p-6">
            <div className="flex items-start gap-2 md:gap-3">
              <i className="ri-information-line text-xl md:text-2xl text-[#F4B942] mt-0.5 md:mt-1"></i>
              <div className="flex-1">
                <p className="text-xs md:text-base text-[#1E1E1E] leading-relaxed">
                  <strong>捐赠后流程:</strong> 支付成功后将自动生成感恩证书,您可以分享证书或申请公益捐赠票据。
                  如有疑问,请查看
                  <button
                    onClick={() => setShowFAQ(true)}
                    className="text-[#2A7A62] font-bold hover:underline cursor-pointer mx-1"
                  >
                    常见问题
                  </button>
                  或联系客服。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Drawer */}
      {showFAQ && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-end animate-[fadeIn_0.3s_ease-out]">
          <div className="w-full max-w-2xl h-full bg-white shadow-2xl overflow-y-auto animate-[slideInRight_0.3s_ease-out]">
            <div className="sticky top-0 bg-white border-b border-[#E0E0E0] p-4 md:p-6 flex items-center justify-between">
              <h3 className="text-xl md:text-2xl font-bold text-[#2A7A62]">常见问题</h3>
              <button
                onClick={() => setShowFAQ(false)}
                className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center hover:bg-[#F9F9F9] rounded-full transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl md:text-2xl text-[#1E1E1E]"></i>
              </button>
            </div>
            <div className="p-4 md:p-6 space-y-4 md:space-y-6">
              {faqData.map((faq, index) => (
                <div key={index} className="bg-[#F9F9F9] rounded-lg md:rounded-xl p-4 md:p-6">
                  <h4 className="text-sm md:text-lg font-bold text-[#1E1E1E] mb-2 md:mb-3 flex items-start gap-2">
                    <i className="ri-question-line text-[#2A7A62] text-lg md:text-xl mt-0.5 md:mt-1"></i>
                    {faq.question}
                  </h4>
                  <p className="text-xs md:text-base text-[#6C6C6C] leading-relaxed pl-6 md:pl-7">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default DonatePage;
